// BuildingStabilityInfoComponent.js
import { useEffect, useState } from "react";
import { SectionHeader, EditableSelectComponent } from "components";
import { AlertInfo, Document, Eye, Plus } from "assets/icons"; // Replace with appropriate icons
import { useSession } from "@clerk/clerk-react";

// Assuming BuildingStabilityInfoDTO is defined elsewhere in your application
import {
  AsbestosPresentStatus,
  BuildingInspectionInfo,
  LeadPresentStatus,
} from "shared/types/building-inspection";
import { BuildingDTO } from "shared/types/building";
import { UnitDTO } from "shared/types/unit";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";
import { FileLabel, FileLog } from "shared/types/file";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
} from "@heroui/react";
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";
import ButtonNew from "components/buttonNew/buttonNew";

interface BuildingDangerousMaterialsInfoComponentProps {
  inspectionInfo: BuildingInspectionInfo;
  handleInspectionInfoChangeInParent?: (value: any) => any;
  handleSaveInParent?: () => any;
  handleCloseParent?: () => any;
  building: BuildingDTO;
  unit: UnitDTO;
  parentEditMode?: boolean;
}

const BuildingDangerousMaterialsInfoComponent = ({
  inspectionInfo,
  handleCloseParent,
  handleInspectionInfoChangeInParent,
  building,
  parentEditMode = true,
}: BuildingDangerousMaterialsInfoComponentProps) => {
  const [editMode, setEditMode] = useState(parentEditMode);
  const [editedInspectionInfo, setEditedInspectionInfo] =
    useState(inspectionInfo);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { session } = useSession();
  const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);

  useEffect(() => {
    setEditedInspectionInfo(inspectionInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch

  const handleSave = async () => {
    setHasUnsavedChanges(false);
    try {
      await fetchWithAuth(
        `${API_URL}/building/building-inspection-info`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editedInspectionInfo),
        },
        session,
      );
      toast.success(
        "Informatie over gevaarlijke stoffen succesvol bijgewerkt!",
      );
    } catch (e) {
      console.error(e);
      toast.error(
        "Er is een fout opgetreden bij het bijwerken van de informatie over gevaarlijke stoffen.",
      );
    }
  };

  const updateInspectionInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedInspectionInfo((prev: any) => ({ ...prev, ...updatedFields }));

    if (handleInspectionInfoChangeInParent) {
      handleInspectionInfoChangeInParent({
        ...inspectionInfo,
        ...updatedFields,
      });
    }
  };

  const getStatusColor = (status: any) => {
    switch (status) {
      case AsbestosPresentStatus.OK:
      case LeadPresentStatus.OK:
        return "bg-green-500";
      case AsbestosPresentStatus.NOT_OK_LESS_URGENT:
      case AsbestosPresentStatus.SUSPICION_PRESENT:
        return "bg-orange-500";
      case LeadPresentStatus.NOT_OK:
      case AsbestosPresentStatus.NOT_OK_URGENT:
        return "bg-red-500";
      default:
        return "bg-transparent";
    }
  };

  const handleOpenAddDangerousMaterialDocumentModal = () => {
    setFileModalOpen(true);
  };

  const handleFileModalClose = () => {
    setFileModalOpen(false);
  };

  const handleFileModalSave = async (fileLogData: any) => {
    const newDangerousMaterialsFileLogs =
      fileLogData.customData?.savedBuildingInspectionInfo
        .dangerousMaterialDocumentsFileLogs;
    if (newDangerousMaterialsFileLogs) {
      setEditedInspectionInfo((prev) => ({
        ...prev,
        dangerousMaterialDocumentsFileLogs: newDangerousMaterialsFileLogs,
      }));
    }
    handleFileModalClose();
  };

  return (
    <section className="flex flex-col gap-0 rounded-md bg-white">
      <SectionHeader
        title="Gevaarlijkes Stoffen Informatie"
        icon={<Document />}
        sticky={false}
        editMode={editMode}
        onEditClick={() => setEditMode(!editMode)}
        onSaveClick={() => handleSave()}
        showEditToggle={true}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseClick={handleCloseParent}
        hideCloseAction={true}
      />
      <div className="p-3">
        <div className="gap-3 sm:grid sm:grid-cols-2">
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.asbestosPresentStatus,
                )}`}
              />

              <p>Asbest aanwezig?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(AsbestosPresentStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.asbestosPresentStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ asbestosPresentStatus: value })
              }
            />
          </div>

          {(editedInspectionInfo?.asbestosPresentStatus ==
            AsbestosPresentStatus.NOT_OK_LESS_URGENT ||
            editedInspectionInfo?.asbestosPresentStatus ==
              AsbestosPresentStatus.NOT_OK_URGENT ||
            editedInspectionInfo?.asbestosPresentStatus ==
              AsbestosPresentStatus.SUSPICION_PRESENT) && (
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <p>Foto's asbesthoudende materialen</p>
              </header>
              <ImagesContainer
                buildingId={building?.id as string}
                fileLabel={FileLabel.AsbestosMaterialsPicture}
              />
            </div>
          )}

          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.leadPresentStatus,
                )}`}
              />

              <p>Loden buizen aanwezig voor wateraanvoer?</p>
              <Tooltip
                classNames={{
                  base: ["z-10"],
                  content: [],
                }}
                content={
                  <div className="px-1 py-2">
                    <div className="font-bold text-small">
                      Loden waterleidingen zijn ongezond
                    </div>
                    <div className="text-tiny">
                      Kleine deeltjes kunnen in het drinkwater terechtkomen.
                    </div>
                    <div className="text-tiny">
                      Kras op de buis met een nagel of paperclip. Indien u een
                      grijze, zilverachtige kleur ziet en een magneet blijft
                      niet hangen aan de buis, dan is het een loden buis.
                    </div>
                  </div>
                }
              >
                <Button
                  className="h-fit w-fit gap-1 bg-transparent font-medium text-moss-primary"
                  startContent={<AlertInfo className="h-[20px] w-[20px]" />}
                />
              </Tooltip>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(LeadPresentStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.leadPresentStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ leadPresentStatus: value })
              }
            />
          </div>
          <div className="col-span-full mt-8 flex flex-col gap-3">
            <header className="flex justify-between px-2.5">
              <div className="flex items-center gap-2">
                <Eye className="h-[18px] w-[18px]" />
                <p className="text-lg">Documenten m.b.t. gevaarlijke stoffen</p>
              </div>
              <ButtonNew
                onClick={handleOpenAddDangerousMaterialDocumentModal}
                icon={<Plus />}
                label="Voeg document toe"
                variant="primaryMoss"
              />
            </header>
            <Table>
              <TableHeader>
                {/* Define table columns */}
                {/* Example: */}
                <TableColumn>Label</TableColumn>
                <TableColumn>Samenvatting</TableColumn>
                <TableColumn>Documentdatum</TableColumn>
                <TableColumn>Sharepoint</TableColumn>
                {/* Other columns as needed */}
              </TableHeader>
              <TableBody>
                {editedInspectionInfo.dangerousMaterialDocumentsFileLogs &&
                  editedInspectionInfo.dangerousMaterialDocumentsFileLogs.map(
                    (fileLog: FileLog) => (
                      <TableRow key={fileLog.id}>
                        <TableCell>
                          {(fileLog.label &&
                            fileLog.label.length > 0 &&
                            fileLog.label[0]) ||
                            ""}
                        </TableCell>
                        <TableCell>{fileLog.summary}</TableCell>
                        <TableCell>
                          {fileLog?.documentDate
                            ? (fileLog?.documentDate as any)
                            : "Datum niet ingevuld"}
                        </TableCell>
                        <TableCell>
                          {fileLog ? (
                            <a
                              href={fileLog.sharePointFileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Eye />
                            </a>
                          ) : (
                            "No Document"
                          )}
                        </TableCell>
                        {/* Other cells as needed */}
                      </TableRow>
                    ),
                  )}
              </TableBody>
            </Table>
          </div>
          {/* Modal for File Upload */}
          <ModalUploadFileLog
            isOpen={fileModalOpen}
            onClose={() => setFileModalOpen(false)}
            onSave={(fileLogData) => handleFileModalSave(fileLogData)}
            building={building}
            incomingCustomData={{
              buildingInspectionInfoId: editedInspectionInfo?.id,
            }}
            customLabels={[
              FileLabel.Asbestattest,
              FileLabel.Asbestinventaris,
              FileLabel.AsbestosRemovalInvoice,
            ]}
          />
        </div>
      </div>
    </section>
  );
};

export default BuildingDangerousMaterialsInfoComponent;
